import React, { useState } from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton,
  Popover,
  Typography,
  Box,
  Paper,
  Grid,
  Tooltip
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface FormattableTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fieldName: string;
}

const FormattableTextField: React.FC<FormattableTextFieldProps> = ({
  value,
  onChange,
  fieldName,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const target = e.target as HTMLTextAreaElement;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      
      // Neuer Text mit Tab an der Cursorposition
      const newValue = value.substring(0, start) + '\t' + value.substring(end);
      
      // Neues Event erzeugen
      const newEvent = {
        target: {
          value: newValue,
          name: fieldName
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(newEvent);
      
      // Wir müssen den Cursor nach dem Tab neu positionieren
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      }, 0);
    }
  };

  return (
    <>
      <TextField
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Formatierungshilfe anzeigen">
                <IconButton 
                  edge="end"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{ mr: 1, mt: 1 }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}
        {...rest}
      />
      
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>Formatierung und Platzhalter</Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Textformatierung:</Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}><Typography><code>_Text_</code></Typography></Grid>
            <Grid item xs={8}><Typography><u>Unterstrichen</u></Typography></Grid>
            
            <Grid item xs={4}><Typography><code>**Text**</code></Typography></Grid>
            <Grid item xs={8}><Typography><i>Kursiv</i></Typography></Grid>
            
            <Grid item xs={4}><Typography><code>***Text***</code></Typography></Grid>
            <Grid item xs={8}><Typography><b>Fett</b></Typography></Grid>
          </Grid>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Verfügbare Platzhalter:</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}><Typography><code>&lt;meldeschluss&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Datum Meldeschluss</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;wettkampfname&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Name des Wettkampfs</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;verein&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Name des Vereins</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;startdatum&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Startdatum</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;enddatum&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Enddatum</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;gesamtbetrag&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Gesamtbetrag der Meldung</Typography></Grid>
            
            <Grid item xs={6}><Typography><code>&lt;datumbuchung&gt;</code></Typography></Grid>
            <Grid item xs={6}><Typography>Meldeschluss + 3 Tage</Typography></Grid>
          </Grid>

          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Hinweis: Du kannst Tab-Tasten zur Einrückung verwenden und Zeilenumbrüche werden beibehalten.
          </Typography>
        </Paper>
      </Popover>
    </>
  );
};

export default FormattableTextField;